import { ref } from '@nuxtjs/composition-api';
import { STEP_TYPE } from '~/constants/StepType';
import useSubscriptions from '~/composable/checkout/validations/useSubscriptions'
import { VALIDATION_STATUS } from '~/constants/ValidationStatus';
import useCheckoutInfo from '~/composable/useCheckoutInfo';
import useTlmCart from '~/composable/useTlmCart';
import { CHECKOUT_FLOW, CUSTOMER_SUBSCRIPTION_TYPE } from '~/constants/CheckoutFlow'
import useCustomer from '~/composable/customer/useCustomer';
import useMitID from '~/composable/useMitID';
import { runPrePaymentSignupValidate, captureOrderValidateFailure } from '~/composable/checkout/handler/orderValidateHelpers';

const appContext = ref(null)

function getChangeSubscriptionPhoneNumber (changeSubscription, getItemAttributesForSubscriptionFromCart) {
  if (changeSubscription.number) {
    return changeSubscription.number
  }
  return getItemAttributesForSubscriptionFromCart('PhoneNumber')
}

function valueOrEmpty (value) {
  if (value) {
    return value
  }
  return ''
}

function getMitIdAccessToken (changeSubscription, getMitIdToken) {
  if (changeSubscription.accessToken) {
    return changeSubscription.accessToken
  }
  const token = getMitIdToken()
  if (token) {
    return token
  }
  return ''
}

function getChangeSubscriptionAccessToken (changeSubscription, getMitIdToken) {
  const accessTokenType = changeSubscription.accessTokenType
  const userName = valueOrEmpty(changeSubscription.userName)
  const password = valueOrEmpty(changeSubscription.password)
  if (!accessTokenType || accessTokenType === 'MIT_ID') {
    return {
      accessTokenType: 'MIT_ID',
      accessToken: getMitIdAccessToken(changeSubscription, getMitIdToken),
      userName,
      password
    }
  }
  return {
    accessTokenType,
    accessToken: valueOrEmpty(changeSubscription.accessToken),
    userName,
    password
  }
}

function buildChangeSubscriptionPayload (state, appCtx) {
  const { subscription } = state
  const { changeSubscription } = subscription
  const {
    subscriptionList,
    mixPoList,
    getCartID,
    isEsimOnlyDevice,
    getItemAttributesForSubscriptionFromCart
  } = useTlmCart(appCtx)
  const { getMitIdToken } = useMitID(appCtx)
  const auth = getChangeSubscriptionAccessToken(changeSubscription, getMitIdToken)

  return {
    ...auth,
    bundleID: subscriptionList.value.sku,
    phoneNumber: getChangeSubscriptionPhoneNumber(changeSubscription, getItemAttributesForSubscriptionFromCart),
    esimOnlyDevice: isEsimOnlyDevice.value,
    cartID: getCartID.value,
    mixServices: mixPoList.value
  }
}

function isChangeSubscriptionValidationFailed (validationResult) {
  if (!validationResult) {
    return true
  }
  if (validationResult.hasErrors) {
    return true
  }
  return validationResult.actionStatus === VALIDATION_STATUS.ERROR
}

export default function deviceAndSubscriptionFlowHandler () {
  const validValidationPromise = () => {
    return Promise.resolve({
      actionStatus: VALIDATION_STATUS.VALID,
      signupBlockingNotifications: [],
      hasSignupBlockingNotification: false
    });
  }
  const initialize = (app) => {
    return new Promise(() => {
      appContext.value = app;
    });
  }

  const preValidate = () => {
    const { preValidateSubscriptions } = useSubscriptions(appContext.value)
    return preValidateSubscriptions();
  }

  const validate = (subscriptionSignUpDataNew) => {
    const { validateSubscriptions } = useSubscriptions(appContext.value)
    return validateSubscriptions(subscriptionSignUpDataNew);
  }

  const execute = () => {
    return validValidationPromise()
  }

  const finalize = () => {
    return validValidationPromise()
  }

  // eslint-disable-next-line complexity
  const finalizeStep = async (stepType, state, appContext) => {
    const customerInfo = state.customerInfo
    const subscription = state.subscription
    const numberInfo = state.numberInfo
    const { addAddressInformation } = useCustomer(appContext)
    const { getCartID, isProtectedAddress, installments, isEsimOnlyDevice } = useTlmCart(appContext)
    const { getMitIdToken } = useMitID(appContext)
    const {
      addPrepaidEmail,
      addNumberInfo,
      addPermissionInformation,
      addEsimOnlyDeviceInformation,
      removeChangeSubscriptionCustomerInfo,
      removeNumberInfo,
      addChangeSubscriptionCustomerInfo
    } = useCheckoutInfo(appContext)
    const { getItemAttributesForSubscriptionFromCart } = useTlmCart(appContext)
    let emailTobeAdded = true
    if (subscription.menu.value === CUSTOMER_SUBSCRIPTION_TYPE.CHANGE_SUBSCRIPTION) {
      emailTobeAdded = installments.value !== 0
    }
    switch (stepType) {
      case STEP_TYPE.SUBSCRIPTION_TYPE: {
        const menuType = subscription.menu.value
        if (menuType === CUSTOMER_SUBSCRIPTION_TYPE.KEEP_SUBSCRIPTION) {
          const prepaidAccountId = getItemAttributesForSubscriptionFromCart('PrepaidAccountId')
          const serviceAccountId = getItemAttributesForSubscriptionFromCart('ServiceAccountId')
          await removeNumberInfo()
          if (prepaidAccountId && serviceAccountId) {
            await addChangeSubscriptionCustomerInfo(prepaidAccountId, serviceAccountId)
          }
          break
        }
        if (menuType === CUSTOMER_SUBSCRIPTION_TYPE.CHANGE_SUBSCRIPTION) {
          await removeNumberInfo()
          await removeChangeSubscriptionCustomerInfo()
          await addChangeSubscriptionCustomerInfo(subscription.changeSubscription.prepaidAccountId, subscription.changeSubscription.serviceAccountId)
          break
        }
        await removeChangeSubscriptionCustomerInfo()
        await removeNumberInfo()
        await addNumberInfo(numberInfo.value)
        break
      }
      case STEP_TYPE.CUSTOMER_INFO_CONFIRMATION:
        {
          const billingAddressDTO = customerInfo.customerDetails.value.addressDTO
          const privatePickup = customerInfo?.privateInfo?.pickupLocation
          const businessPickup = customerInfo?.businessInfo?.pickupLocation
          const pickup = privatePickup || businessPickup

          const hasPickupAddress = Boolean(
            pickup &&
            pickup.streetName &&
            pickup.streetNumber &&
            pickup.postalCode &&
            pickup.city
          )

          const shippingAddressDTO = hasPickupAddress
            ? {
              // shipping is NOT a person, it's the pakkeshop
                firstName: pickup.name,
                lastName: '',
                company: '',
                streetName: pickup.streetName,
                houseNumber: pickup.streetNumber,
                postalCode: pickup.postalCode,
                postalDistrict: pickup.city,
                floor: '',
                side: '',
                dropPointName: pickup.name,
                dropPointId: pickup.id,
                dropPointDistance: pickup.distance
              }
            : billingAddressDTO

          const addressPayload = {
            useBillingAddressAsShipping: !hasPickupAddress,
            shippingAddress: shippingAddressDTO,
            billingAddress: billingAddressDTO,
            emailAddress: customerInfo.customerDetails.value.email,
            cartID: getCartID.value
          }

          if (isProtectedAddress.value) {
            addressPayload.tempAddressIdentifier = getMitIdToken()
          }

          await addAddressInformation(addressPayload);
        }
        await addPermissionInformation({
          permissionEmail: customerInfo.permissionsAccepted.value,
          permissionSms: customerInfo.permissionsAccepted.value,
          permissionCall: customerInfo.permissionsAccepted.value,
          permissionTermsText: customerInfo.permissionText1 + customerInfo.permissionText2 + customerInfo.linkText1
        })
        if (isEsimOnlyDevice.value) {
          await addEsimOnlyDeviceInformation({
            esimOnlyDevice: isEsimOnlyDevice.value,
            currentSimType: subscription?.menu?.value === CUSTOMER_SUBSCRIPTION_TYPE.NEW_NUMBER ? subscription.newNumber.currentSimType : subscription.moveNumber.currentSimType
          })
        }
        if (emailTobeAdded) {
          await addPrepaidEmail(customerInfo.customerDetails.value.email)
        }
        break;
      case STEP_TYPE.CONFIRMATION: {
        const menuType = subscription.menu.value
        const isSignUp = menuType === CUSTOMER_SUBSCRIPTION_TYPE.NEW_NUMBER ||
          menuType === CUSTOMER_SUBSCRIPTION_TYPE.MOVE_NUMBER
        const isChangeSub = menuType === CUSTOMER_SUBSCRIPTION_TYPE.CHANGE_SUBSCRIPTION ||
          menuType === CUSTOMER_SUBSCRIPTION_TYPE.KEEP_SUBSCRIPTION

        if (isSignUp) {
          const validated = await runPrePaymentSignupValidate(
            state,
            appContext,
            CHECKOUT_FLOW.DEVICE_WITH_SUBSCRIPTION
          )
          if (!validated) {
            return false
          }
          return true
        }

        if (isChangeSub) {
          const { validateChangeSubscription } = useSubscriptions(appContext)
          const changeSubPayload = buildChangeSubscriptionPayload(state, appContext)
          if (!changeSubPayload.phoneNumber) {
            return false
          }
          const changeSubResult = await validateChangeSubscription(changeSubPayload)
          if (isChangeSubscriptionValidationFailed(changeSubResult)) {
            captureOrderValidateFailure(changeSubResult)
            return false
          }
          return true
        }

        return true
      }
      default:
        return true;
    }
  }

  return {
    initialize,
    preValidate,
    validate,
    execute,
    finalize,
    finalizeStep
  }
}
